import { useRef, useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import THEME from '../../../constants/theme';
import { glassEffect } from '../../../styles';
import VideoModal from '../../common/VideoModal';
import useStrategyPerformance from '../../../hooks/useStrategyPerformance';
const heroVideo =
  'https://www.dropbox.com/scl/fi/76iqr8o4lqko7hpmqk874/hero-video-01.mp4?rlkey=1el0debt5fq926i9yulmzsg0l&st=aqm5419q&raw=1';

/**
 * HeroBanner component for the homepage
 *
 * @returns {React.Component} HeroBanner component
 */
const HeroBanner = () => {
  const videoRef = useRef(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const { performance: strandQPerformance } =
    useStrategyPerformance('strand-q');
  const { performance: merinPerformance } = useStrategyPerformance('merin');
  const { performance: dravonPerformance } = useStrategyPerformance('dravon');

  const companyVideoUrl =
    'https://www.dropbox.com/scl/fi/ra9e6lqlf3n1wlqf9o5q9/MarQ-intro.mp4?rlkey=sr7b1m14c3tr5ks1rzkgnl5b6&st=ficeba75&raw=1';

  // Use useEffect to ensure video plays correctly after component mounts
  useEffect(() => {
    if (videoRef.current) {
      // Ensure video loads properly
      videoRef.current.load();

      // Play video after load - important for some browsers
      const playPromise = videoRef.current.play();

      // Handle play promise to avoid errors
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Video autoplay failed - this is expected behavior in many browsers
        });
      }
    }
  }, []);

  const strandQHeroBanner = strandQPerformance?.heroBanner || {
    totalReturnCumulative: '+59.26%',
    lastMonthLabel: 'Jan',
    lastMonthValue: '+0.37%',
    inceptionDate: '19 Jan 2024'
  };

  const merinHeroBanner = merinPerformance?.heroBanner || {
    totalReturnCumulative: '1.82%',
    lastMonthLabel: 'Jan',
    lastMonthValue: '-1.02%',
    inceptionDate: '10 Nov 2025'
  };

  const dravonHeroBanner = dravonPerformance?.heroBanner || {
    totalReturnCumulative: '+48.57%',
    lastMonthLabel: 'Jan',
    lastMonthValue: '+4.37%',
    inceptionDate: '01 Sep 2025'
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: { xs: '8vh', sm: '30vh' },
        justifyContent: 'flex-start',
        color: THEME.COLORS.NEUTRAL_WHITE,
        overflow: 'hidden',
        '@keyframes pulse': {
          '0%, 100%': {
            transform: 'scale(1)'
          },
          '50%': {
            transform: 'scale(1.02)'
          }
        }
      }}
    >
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          left: '50%',
          top: '50%',
          objectFit: 'cover',
          transform: 'translate(-50%, -50%)',
          zIndex: 0 // Changed from -1 to 0
        }}
      >
        <source src={heroVideo} type='video/mp4' />
        Your browser does not support the video tag.
      </video>

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1
        }}
      />

      {/* Text Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          padding: { xs: '0 2rem', sm: '0 4rem', md: '0 6rem', lg: '0 8rem' },
          width: '100%',
          maxWidth: '1400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'center', md: 'flex-start' }
        }}
      >
        {/* Main Title */}
        <Box
          component='h1'
          sx={{
            fontSize: {
              xs: '1rem',
              sm: '1.4rem',
              md: '1.75rem',
              lg: '2.1rem'
            },
            fontWeight: 400,
            marginBottom: { xs: '0.5rem', md: '1rem' },
            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            lineHeight: { xs: 1.2, md: 1.15 },
            width: { xs: '100%', md: '75%', lg: '70%' },
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          Providing systematic macro-agnostic <br />
          strategies for Institutional portfolios
        </Box>

        {/* Subtitle */}
        <Box
          sx={{
            fontSize: { xs: '1.05rem', sm: '1.2rem', md: '1.35rem' },
            fontWeight: 300,
            marginBottom: { xs: '2rem', md: '2.5rem' },
            textShadow: '0 2px 10px rgba(0,0,0,0.3)',
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            lineHeight: 1.6,
            width: { xs: '100%', md: '75%', lg: '70%' },
            textAlign: { xs: 'center', md: 'left' }
          }}
        >
          Near-zero correlation to Global Markets with full operational
          transparency.
          <br />
          Delivered via custodian-segregated AMCs & SMA: daily liquidity, no
          lock-ups.
        </Box>

        {/* Features Label with divider styling */}
        <Box
          sx={{
            ...glassEffect(),
            marginBottom: { xs: '1rem', md: '1.5rem' },
            width: { xs: '100%', md: '75%', lg: '70%' }
          }}
        >
          <Box
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' },
              fontWeight: 400,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              letterSpacing: '0.3px',
              lineHeight: 1.6,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: '0.5rem', sm: '0.5rem' }
            }}
          >
            {/* 100% Systematic */}
            <Box
              component='span'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Box
                component='span'
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.2rem',
                  lineHeight: 1
                }}
              >
                •
              </Box>
              100% Systematic
            </Box>

            {/* T+2 Settlement */}
            <Box
              component='span'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Box
                component='span'
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.2rem',
                  lineHeight: 1
                }}
              >
                •
              </Box>
              T+2 Settlement
            </Box>

            {/* Segregated Accounts */}
            <Box
              component='span'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Box
                component='span'
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.2rem',
                  lineHeight: 1
                }}
              >
                •
              </Box>
              Segregated Accounts
            </Box>

            {/* Fully Transparent */}
            <Box
              component='span'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Box
                component='span'
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  fontSize: '1.2rem',
                  lineHeight: 1
                }}
              >
                •
              </Box>
              <Box component='span'>
                Fully Transparent in-house live monitoring platform
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Program Performance Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'center' },
            gap: { xs: '1rem', sm: '1.5rem' },
            width: { xs: '100%', md: '75%', lg: '70%' },
            justifyContent: { xs: 'center', md: 'flex-start' },
            flexWrap: 'wrap'
          }}
        >
          {/* StrandQ Card */}
          <Box
            component='a'
            href='https://mqcapital.co.uk/strategies/strand-q'
            sx={{
              ...glassEffect(),
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.35rem',
              borderRadius: '10px',
              padding: { xs: '0.7rem 1rem', sm: '0.8rem 1.2rem' },
              animation: 'pulse 2s ease-in-out infinite',
              textDecoration: 'none',
              color: THEME.COLORS.NEUTRAL_WHITE,
              transition: 'all 0.3s ease',
              '&:hover': {
                textDecoration: 'none',
                transform: 'translateY(-3px)',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.35)'
              },
              '&:focus': { textDecoration: 'none' },
              '&:active': { textDecoration: 'none' },
              '&:visited': { textDecoration: 'none' }
            }}
            aria-label='View StrandQ performance details'
          >
            <Box
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: 600,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textTransform: 'uppercase',
                letterSpacing: '0.6px'
              }}
            >
              StrandQ
            </Box>
            <Box
              sx={{
                fontSize: { xs: '1.35rem', sm: '1.55rem', md: '1.8rem' },
                fontWeight: 700,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textShadow: '0 2px 8px rgba(255, 255, 255, 0.3)',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              {strandQHeroBanner.totalReturnCumulative}
              <Tooltip
                title={`Inception: ${strandQHeroBanner.inceptionDate}`}
                placement='top'
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
                    color: THEME.COLORS.NEUTRAL_BLACK,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '0.5rem 0.75rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  },
                  '& .MuiTooltip-arrow': {
                    color: THEME.COLORS.NEUTRAL_WHITE
                  }
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    cursor: 'pointer',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                letterSpacing: '0.5px'
              }}
            >
              {`${strandQHeroBanner.lastMonthLabel}: ${strandQHeroBanner.lastMonthValue}`}
            </Box>
            <Box
              component='span'
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 400,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textDecoration: 'none'
              }}
            >
              Read more
            </Box>
          </Box>

          {/* Merin Card */}
          <Box
            component='a'
            href='https://mqcapital.co.uk/strategies/merin'
            sx={{
              ...glassEffect(),
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.35rem',
              borderRadius: '10px',
              padding: { xs: '0.7rem 1rem', sm: '0.8rem 1.2rem' },
              animation: 'pulse 2s ease-in-out infinite',
              textDecoration: 'none',
              color: THEME.COLORS.NEUTRAL_WHITE,
              transition: 'all 0.3s ease',
              '&:hover': {
                textDecoration: 'none',
                transform: 'translateY(-3px)',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.35)'
              },
              '&:focus': { textDecoration: 'none' },
              '&:active': { textDecoration: 'none' },
              '&:visited': { textDecoration: 'none' }
            }}
            aria-label='View Merin performance details'
          >
            <Box
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: 600,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textTransform: 'uppercase',
                letterSpacing: '0.6px'
              }}
            >
              Merin
            </Box>
            <Box
              sx={{
                fontSize: { xs: '1.35rem', sm: '1.55rem', md: '1.8rem' },
                fontWeight: 700,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textShadow: '0 2px 8px rgba(255, 255, 255, 0.3)',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              {merinHeroBanner.totalReturnCumulative}
              <Tooltip
                title={`Inception: ${merinHeroBanner.inceptionDate}`}
                placement='top'
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
                    color: THEME.COLORS.NEUTRAL_BLACK,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '0.5rem 0.75rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  },
                  '& .MuiTooltip-arrow': {
                    color: THEME.COLORS.NEUTRAL_WHITE
                  }
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    cursor: 'pointer',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                letterSpacing: '0.5px'
              }}
            >
              {`${merinHeroBanner.lastMonthLabel}: ${merinHeroBanner.lastMonthValue}`}
            </Box>
            <Box
              component='span'
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 400,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textDecoration: 'none'
              }}
            >
              Read more
            </Box>
          </Box>

          {/* Dravon Card */}
          <Box
            component='a'
            href='https://mqcapital.co.uk/strategies/dravon'
            sx={{
              ...glassEffect(),
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.35rem',
              borderRadius: '10px',
              padding: { xs: '0.7rem 1rem', sm: '0.8rem 1.2rem' },
              animation: 'pulse 2s ease-in-out infinite',
              textDecoration: 'none',
              color: THEME.COLORS.NEUTRAL_WHITE,
              transition: 'all 0.3s ease',
              '&:hover': {
                textDecoration: 'none',
                transform: 'translateY(-3px)',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.35)'
              },
              '&:focus': { textDecoration: 'none' },
              '&:active': { textDecoration: 'none' },
              '&:visited': { textDecoration: 'none' }
            }}
            aria-label='View Dravon performance details'
          >
            <Box
              sx={{
                fontSize: { xs: '0.85rem', sm: '0.95rem' },
                fontWeight: 600,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textTransform: 'uppercase',
                letterSpacing: '0.6px'
              }}
            >
              Dravon
            </Box>
            <Box
              sx={{
                fontSize: { xs: '1.35rem', sm: '1.55rem', md: '1.8rem' },
                fontWeight: 700,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textShadow: '0 2px 8px rgba(255, 255, 255, 0.3)',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem'
              }}
            >
              {dravonHeroBanner.totalReturnCumulative}
              <Tooltip
                title={`Inception: ${dravonHeroBanner.inceptionDate}`}
                placement='top'
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
                    color: THEME.COLORS.NEUTRAL_BLACK,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '0.5rem 0.75rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                  },
                  '& .MuiTooltip-arrow': {
                    color: THEME.COLORS.NEUTRAL_WHITE
                  }
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    cursor: 'pointer',
                    opacity: 0.8,
                    '&:hover': { opacity: 1 }
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                color: THEME.COLORS.NEUTRAL_WHITE,
                letterSpacing: '0.5px'
              }}
            >
              {`${dravonHeroBanner.lastMonthLabel}: ${dravonHeroBanner.lastMonthValue}`}
            </Box>
            <Box
              component='span'
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 400,
                color: THEME.COLORS.NEUTRAL_WHITE,
                textDecoration: 'none'
              }}
            >
              Read more
            </Box>
          </Box>
        </Box>

        {/* Learn More About Our Company Link */}
        <Box
          sx={{
            width: { xs: '100%', md: '75%', lg: '70%' },
            display: 'flex',
            justifyContent: { xs: 'center', md: 'flex-start' },
            marginTop: { xs: '2rem', sm: '2.5rem' }
          }}
        >
          <Box
            component='span'
            onClick={() => setVideoModalOpen(true)}
            sx={{
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
              fontWeight: 400,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              color: THEME.COLORS.NEUTRAL_WHITE,
              cursor: 'pointer',
              position: 'relative',
              textDecoration: 'none',
              transition: 'all 0.3s ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: '0%',
                height: '1px',
                bottom: '-2px',
                left: '50%',
                backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
                transition: 'all 0.3s ease',
                transform: 'translateX(-50%)'
              },
              '&:hover::after': {
                width: '100%'
              },
              '&:hover': {
                textShadow: '0 2px 8px rgba(255, 255, 255, 0.3)'
              }
            }}
            role='button'
            tabIndex={0}
            onKeyPress={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                setVideoModalOpen(true);
              }
            }}
            aria-label='Learn more about our company - Watch video'
          >
            Learn more about our company
          </Box>
        </Box>
      </Box>

      {/* Video Modal */}
      <VideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        videoUrl={companyVideoUrl}
      />
    </Box>
  );
};

export default HeroBanner;
